import lodash from 'lodash'
import { CommentTypes, CommentActionTypes, CommentObject } from '@/modules/tenantProjects/submissions/consts'
import * as tenantProjectApi from '@/api/tenantProjects/request'
import types from '@/store/mutation-types'
import { checkEmptyAccount } from '@/modules/tenantProjects/information/consts'
import { forceUpdateVueObject, updateVueObject } from '@/store/util'
import { getAttachments } from '@/api/docs/request'
import { getKey } from '@/api/common/request'
import { techCommentLineTypes, tenantProjectStatus } from '@/utils/locale'
import { getProjectName } from '@/modules/tenantProjects/utils'
import { RepAccessTypes } from '@/modules/tenantProjects/contractors/consts'
import { DOCS_SORT_RULES } from '@/api/docs/consts'
import * as insepctionAPI from '@/api/inspection/request'

function findIndex (arr, obj, attr) {
  let result = -1
  if (Array.isArray(arr)) {
    arr.forEach((item, index) => {
      if (item[attr] === obj[attr] && result === -1) {
        result = index
      }
    })
  }
  return result
}

function findIndexForInsert (arr, obj, attr) {
  let result = -1
  if (Array.isArray(arr)) {
    arr.forEach((item, index) => {
      if (Number(item[attr]) < Number(obj[attr])) {
        result = index
      }
    })
  }
  return result
}
function getSequence (lines) {
  const lastItemSeq = lines.length > 0 ? lines[lines.length - 1].order : 0
  return lastItemSeq + 1
}

async function getNewLine (section, value) {
  const date = new Date()
  const id = await getKey()
  const itemId = await getKey()
  return {
    id,
    itemId,
    value,
    type: techCommentLineTypes.USER,
    order: getSequence(section.sectionContent),
    section: section.value,
    parentId: section.itemId,
    createdAt: date,
    updatedAt: date,
  }
}

function getCommentTypes (getters, userId) {
  return (userId === deep(getters, 'currentDesignReviewer.id') ? [CommentTypes.DESIGN] : [])
    .concat(userId === deep(getters, 'currentTechReviewer.id') ? [CommentTypes.TECH] : [])
}

/**
 * @typedef Refund
 * @prop {RefundHistory[]} actionHistory
 * @prop {string} requestedAt
 * @prop {nuber} amount
 * @prop {boolean} isDirect
 * @prop {string} payeeName
 * @prop {string} address
 * @prop {string} sourceId
 */
/**
 * @typedef RefundHistory
 * @prop {string} actionType
 * @prop {string} id
 * @prop {string} updatedAt
 */
/**
 * @typedef Submission
 * @prop {object[]} actionHistory
 * @prop {string} createdAt
 * @prop {string} createdBy
 * @prop {object} designComment
 * @prop {string} designManagedBy
 * @prop {object} designManager
 * @prop {string} id
 * @prop {number} order
 * @prop {string} projectId
 * @prop {object} submittedDesignComment
 * @prop {object} submittedTechComment
 * @prop {object} techComment
 * @prop {string} techManagedBy
 * @prop {object} techManager
 * @prop {string} updatedAt
 */
/**
 * @typedef State
 * @prop {Object} currentTenantProject
 * @prop {string} currentTenantProject.status
 * @prop {string} currentTenantProject.type
 * @prop {string} currentTenantProject.projectName
 * @prop {Refund} currentTenantProject.refund
 * @prop {Refund} refund
 * @prop {any[]} currentTenantProject.account
 * @prop {Submission[]} currentTenantProject.submissions
 */
const defaultState = getDefaultState()

/**
 * @returns {State}
 */
function getDefaultState () {
  return {
    currentTenantProject: {
      accounts: [],
    },
    lastSearchedAttributes: {},
    lastSearchKeys: {},
    allTenantProjects: [],
    /** The following two are used in the all tenant projects page */
    lastSearchedAttributesAll: {},
    allTenantProjectsAll: [],
    startReview: false,
    deposit: {},
    management: {},
    refund: {},
    inspections: [],
  }
}

const getters = {
  /**
   * @param {State} state
   */
  currentTenantProject: state => state.currentTenantProject,
  /**
   * @param {State} state
   */
  activeSubmission: (state, getters) => {
    if (getters.currentTenantSubmission && !getters.currentTenantSubmission.completedDate) {
      return getters.currentTenantSubmission
    }
    return null
  },
  activeSubmissionIndex: (state, getters) => {
    return findIndex(state.currentTenantProject.submissions, getters.currentTenantSubmission, 'id')
  },
  /**
   * @param {State} state
   * @return {Submission}
   */
  currentTenantSubmission: state => (deep(state, 'currentTenantProject.submissions') || []).concat().sort((submissionA, submissionB) => {
    return submissionB.order - submissionA.order
  })[0],
  hasPendingSubmission: (_, getters) => getters.currentTenantSubmission && !getters.currentTenantSubmission.completedDate,
  currentDesignReviewer: (_, getters) => getters.hasPendingSubmission && getters.currentTenantSubmission.designManager,
  currentTechReviewer: (_, getters) => getters.hasPendingSubmission && getters.currentTenantSubmission.techManager,
  accounts: state => {
    const account = [{
      TenantProjectAccount: {
        contactFirstName: '',
        contactLastName: '',
        phone: '',
        altPhone: '',
      },
      username: '',
    }]
    return lodash.isEmpty(lodash.get(state, 'currentTenantProject.accounts', [])) ? account : lodash.get(state, 'currentTenantProject.accounts')
  },
  accountsReturnEmptyListIfEmpty: state => {
    const accounts = lodash.get(state, 'currentTenantProject.accounts', [])
    return lodash.orderBy(accounts, (item) => lodash.get(item, 'TenantProjectAccount.isContractorAdmin', false), 'desc')
  },
  currentAccount: (state, getters, rootState, rootGetters) => {
    const currentUser = rootGetters['user/currentUser'] || {}
    return (getters.accounts || []).find(account => account.id === currentUser.id)
  },
  isNormalAccount: (state, getters) => {
    return lodash.get(getters.currentAccount, 'TenantProjectAccount.accessType') !== RepAccessTypes.CONTRACTOR
  },
  isMainContractor: (state, getters) => {
    const isContractorAccount = lodash.get(getters.currentAccount, 'TenantProjectAccount.accessType') === RepAccessTypes.CONTRACTOR
    const isAdmin = lodash.get(getters.currentAccount, 'TenantProjectAccount.isContractorAdmin')
    return isAdmin && isContractorAccount
  },
  lastSearchedAttributes: state => state.lastSearchedAttributes,
  allTenantProjects: state => state.allTenantProjects,
  lastSearchedAttributesAll: state => state.lastSearchedAttributesAll,
  lastSearchKeys: state => state.lastSearchKeys,
  allTenantProjectsAll: state => state.allTenantProjectsAll,
  canTenantRead: (state, getters, rootState, rootGetters) => {
    const canAccessAllProjects = rootGetters['ccd/canAccessAllProjects']
    const user = rootGetters['user/currentUser'] || {}
    return canAccessAllProjects || getters.accounts.some(account => account.id && account.id === user.id)
  },
  canTenantUpdate: (state, getters) => {
    return getters.canTenantRead
  },
  startReview: state => state.startReview,
  currentTenantProjectDocs: state => lodash.get(state, 'currentTenantProject.projectDocs', []),
  deposit: state => lodash.get(state, 'currentTenantProject.deposit'),
  /**
   * @param {State} state
   */
  refund: state => state.currentTenantProject.refund,
  management: state => lodash.get(state, 'currentTenantProject.management'),
  inspections: state => state.inspections,
  finalDocument: state => lodash.get(state, 'currentTenantProject.finalDocument', {}),
  isWithdrawn: state => lodash.get(state, 'currentTenantProject.status') === tenantProjectStatus.WITHDRAWN,
  isCompleted: state => lodash.get(state, 'currentTenantProject.status') === tenantProjectStatus.COMPLETED,
}

const actions = {
  async createTenantProject ({ commit }, {currentTenantProject, isAccept}) {
    const project = await tenantProjectApi.createTenantProject(currentTenantProject, isAccept)
    return project
  },
  async loadTenantProject ({ commit, dispatch }, tenantProjectId) {
    const project = await tenantProjectApi.loadTenantProject(tenantProjectId)
    if (!project) {
      return
    }
    project.projectName = getProjectName(project, project.tenancy)
    commit(types.TENANT_PROJECT_CURRENT, { project })
    if (project.projectDocsSrcId) {
      await dispatch('loadTenantProjectsDocs', { projectDocsSrcId: project.projectDocsSrcId })
    }
    if (lodash.get(project, 'workPermits.length')) {
      dispatch('workPermit/setWorkPermitsForTenantProject', { permits: project.workPermits, needRoot: true }, { root: true })
    }
    dispatch('projectContractors/setContractors', { project, needRoot: true, isTms: true }, { root: true })
    commit(types.TENANT_PROJECT_DEPOSIT, project.deposit)
    commit(types.TENANT_PROJECT_REFUND, project.refund)
    if (project.management) {
      commit(types.TENANT_PROJECT_MANAGEMENT, project.management)
    }
    return project
  },
  changeCurrentProject ({ commit }, project) {
    commit(types.TENANT_PROJECT_CURRENT, { project })
  },
  async loadTenantProjectsDocs ({ commit }, { projectDocsSrcId }) {
    if (projectDocsSrcId) {
      const projectDocsSrcIdFiles = await getAttachments(projectDocsSrcId, false, DOCS_SORT_RULES.TENANT_PROJECT_DOCS)
      commit(types.TENANT_PROJECT_UPDATE_FIELD_PATH, {
        path: ['projectDocs'],
        newValue: lodash.cloneDeep(projectDocsSrcIdFiles),
      })
      return projectDocsSrcIdFiles
    }
    return []
  },
  async isUpdateable ({ commit }, tenantProjectId) {
    const isUpdateable = await tenantProjectApi.isUpdateable(tenantProjectId)
    if (!isUpdateable) {
      const project = await tenantProjectApi.loadTenantProject(tenantProjectId)
      commit(types.TENANT_PROJECT_CURRENT, { project })
    }
    return isUpdateable
  },
  async updateTenantProjectAllInfo ({ state }, needNotify) {
    await tenantProjectApi.updateTenantProjectAllInfo(state.currentTenantProject, needNotify)
  },
  async updateAccounts ({ state }, needNotify) {
    await tenantProjectApi.updateAccounts(state.currentTenantProject, needNotify)
  },
  async updateTenantProjectStatus ({ state, commit }, { request, action }) {
    await tenantProjectApi.updateTenantProjectStatus(state.currentTenantProject.id, request, action)
    commit(types.TENANT_PROJECT_UPDATE_FIELD_NAME, { name: 'status', value: request.status })
  },
  async addTechCommentLine ({ state, getters, commit, dispatch }, {section, line, indexBefore}) {
    const submission = getters.activeSubmission
    const index = getters.activeSubmissionIndex
    const targetSection = lodash.cloneDeep(section)
    let targetLine

    let comments = lodash.cloneDeep(submission.techComment.comments)
    if (!comments) {
      comments = []
    }
    const sectionIndex = findIndex(comments, section, 'id')
    if (sectionIndex < 0) {
      // Insert the guideline section that is not existing in the tech comments
      targetSection.sectionContent = [lodash.cloneDeep(line)]
      const indexForInsert = lodash.findLastIndex(comments, { order: targetSection - 1 })
      targetSection.secondaryOrder = 0
      comments.splice(indexForInsert + 1, 0, targetSection)
    } else {
      targetLine = line.id ? lodash.cloneDeep(line) : await getNewLine(targetSection, line.value)
      const sectionContent = comments[sectionIndex].sectionContent
      const indexForInsertLine = typeof indexBefore === 'undefined' ? findIndexForInsert(sectionContent, targetLine, 'order') : indexBefore
      sectionContent.splice(indexForInsertLine + 1, 0, targetLine)
    }
    commit(types.TENANT_PROJECT_UPDATE_FIELD_PATH,
      {path: ['submissions', index, 'techComment', 'comments'], newValue: comments})
    dispatch('logSubmissionAction', {
      actionType: line.id ? CommentActionTypes.CHECK : CommentActionTypes.ADD,
      commentObject: CommentObject.COMMENT,
      guidelineId: (targetLine || {}).itemId || line.itemId,
    })
    return targetLine
  },
  removeTechCommentLine ({ state, getters, commit, dispatch }, {section, line}) {
    const submission = getters.activeSubmission
    const index = getters.activeSubmissionIndex
    const targetLine = lodash.cloneDeep(line)

    if (!submission.techComment || !submission.techComment.comments) {
      return
    }
    const comments = lodash.cloneDeep(submission.techComment.comments)
    const sectionIndex = findIndex(comments, section, 'itemId')
    if (sectionIndex < 0) {
      return
    } else {
      const sectionContent = comments[sectionIndex].sectionContent
      const lineIndex = findIndex(sectionContent, targetLine, 'itemId')
      sectionContent.splice(lineIndex, 1)
      if (sectionContent.length === 0) {
        comments.splice(sectionIndex, 1)
      }
    }
    commit(types.TENANT_PROJECT_UPDATE_FIELD_PATH,
      {path: ['submissions', index, 'techComment', 'comments'], newValue: comments})
    dispatch('removeSubmissionAction', {
      submissionIdx: index,
      actionType: CommentActionTypes.CHECK,
      guidelineId: line.itemId,
    })
  },
  async createTenantSubmission ({ commit }, {projectId, submission}) {
    const project = await tenantProjectApi.createTenantSubmission(projectId, submission)
    return project
  },
  updateCurrentStateSubmissionByPath ({getters, commit}, {path, newValue}) {
    const submissionIdx = getters.currentTenantProject.submissions.findIndex(submission => getters.currentTenantSubmission.id === submission.id)
    commit(types.TENANT_PROJECT_UPDATE_FIELD_PATH, {
      path: ['submissions', submissionIdx].concat(path),
      newValue,
    })
  },
  updateSubmissionByPath ({getters, commit}, {path, newValue, submissionIdx}) {
    commit(types.TENANT_PROJECT_UPDATE_FIELD_PATH, {
      path: ['submissions', submissionIdx].concat(path),
      newValue,
    })
  },
  async updateCurSubmissionExpDesign ({ getters, commit }, { needNotificate, auditLogAction }) {
    const curSubmissionWithOnlyTech = lodash.cloneDeep(getters.currentTenantSubmission)
    delete curSubmissionWithOnlyTech.designComment
    delete curSubmissionWithOnlyTech.submittedDesignComment
    await tenantProjectApi.updateTenantSubmission(curSubmissionWithOnlyTech, needNotificate, auditLogAction)
    const newValue = await tenantProjectApi.getTenantSubmission(curSubmissionWithOnlyTech.id)
    commit(types.SET_TENANT_PROJECT_SUBMISSION_BY_ID, {
      sid: curSubmissionWithOnlyTech.id,
      newValue,
    })
  },
  async updateCurrentTenantSubmission ({ getters }, { needNotificate, auditLogAction }) {
    await tenantProjectApi.updateTenantSubmission(getters.currentTenantSubmission, needNotificate, auditLogAction)
  },
  async updateCurrentSubmissionBatchStatus ({ getters }, auditLogAction) {
    await tenantProjectApi.updateSubmissionBatchStatus(getters.currentTenantSubmission, auditLogAction)
  },
  async updateTenantSubmission ({ getters }, indexOfSubmission) {
    await tenantProjectApi.updateTenantSubmission(getters.currentTenantProject.submissions[indexOfSubmission])
  },
  logResentAction ({getters, dispatch, rootGetters}, actionRecord) {
    const user = rootGetters['user/currentUser']
    const userId = user.id
    dispatch('updateSubmissionByPath', {
      path: ['actionHistory'],
      submissionIdx: actionRecord.submissionIndex,
      newValue: [{
        actionType: CommentActionTypes.UPDATE,
        user: {
          id: userId,
          username: user.username,
          displayName: user.displayName,
        },
        commentTypes: getCommentTypes(getters, userId),
        batchOrder: getters.currentTenantSubmission.order,
        commentObject: CommentObject.SUBMISSION,
        isNewRec: true,
        ...actionRecord.content,
      }].concat(getters.currentTenantProject.submissions[actionRecord.submissionIndex].actionHistory),
    })
  },
  logSubmissionAction ({getters, dispatch, rootGetters}, actionRecord) {
    const user = rootGetters['user/currentUser']
    const userId = user.id
    dispatch('updateCurrentStateSubmissionByPath', {
      path: ['actionHistory'],
      newValue: [{
        actionType: CommentActionTypes.UPDATE,
        user: {
          id: userId,
          username: user.username,
          displayName: user.displayName,
        },
        commentTypes: getCommentTypes(getters, userId),
        batchOrder: getters.currentTenantSubmission.order,
        commentObject: CommentObject.SUBMISSION,
        isNewRec: true,
        ...actionRecord,
      }].concat(getters.currentTenantSubmission.actionHistory),
    })
  },
  removeSubmissionAction ({getters, dispatch, rootGetters}, removeRecord) {
    const submission = getters.currentTenantProject.submissions[removeRecord.submissionIdx]
    submission.actionHistory.forEach((action, index) => {
      if (action.actionType === removeRecord.actionType &&
        action.guidelineId === removeRecord.guidelineId) {
        dispatch('updateSubmissionByPath', {
          path: ['actionHistory', index],
          submissionIdx: removeRecord.submissionIdx,
          newValue: Object.assign(getters.currentTenantProject.submissions[removeRecord.submissionIdx].actionHistory[index], { isDeleteRec: true }),
        })
      }
    })
  },
  async loadAllTenantProjects ({ commit, state, dispatch }, query) {
    const allTenantProjects = await tenantProjectApi.listAll(query)
    commit(types.TENANT_PROJECT_LOAD_ALL, { allTenantProjects })
  },
  async loadViewAllTenantProjects ({ commit, state, dispatch }, { query, diskCacheKey }) {
    const allTenantProjects = await tenantProjectApi.listAll(query, diskCacheKey)
    commit(types.TENANT_PROJECT_LOAD_VIEW_ALL, { allTenantProjects })
  },
  setLastSearchedAttributes ({ commit }, lastSearchedAttributes) {
    const attributes = lodash.cloneDeep(lastSearchedAttributes)
    commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: attributes })
  },
  setLastSearchedAttributesAll ({ commit }, lastSearchedAttributesAll) {
    const attributes = lodash.cloneDeep(lastSearchedAttributesAll)
    commit(types.SET_LAST_SEARCH_ATTRIBUTES_ALL, { lastSearchedAttributesAll: attributes })
  },
  async resetQueryAllIfNotMatch ({ commit, state }, tenancyId) {
    let prevTenancyId = deep(state, ['allTenantProjects', 0, 'tenancyId'])
    if (prevTenancyId && (Number(prevTenancyId) !== tenancyId)) {
      commit(types.TENANT_PROJECT_LOAD_ALL, { allTenantProjects: [] })
      commit(types.SET_LAST_SEARCH_ATTRIBUTES, { lastSearchedAttributes: {} })
    }
  },
  async updateFinalDocument ({ getters, commit }, request) {
    await tenantProjectApi.updateFinalDocument(getters.finalDocument.id, request)
    const finalDocument = await tenantProjectApi.getFinalDocument(getters.finalDocument.id)
    commit(types.SET_CURR_TENANT_PROJECT_FINALDOCUMENT, { finalDocument })
  },
  async loadFinalDocumentHistories ({ getters, commit }) {
    const histories = await tenantProjectApi.getFinalDocsHistories(getters.finalDocument.id)
    commit(types.SET_CURR_TENANT_PROJECT_FINALDOCUMENT, { finalDocument: { ...getters.finalDocument, histories } })
  },
  setLastSearchKeys ({ state, commit }, lastSearchKeys) {
    commit(types.SET_LAST_SEARCHED_KEYS, { lastSearchKeys })
  },
  async loadAllInspections ({state, commit}, projectId) {
    const result = await insepctionAPI.getALL({
      projectId: projectId,
    })
    const inspectionData = lodash.orderBy(result, ['date', 'createdAt'], ['desc', 'asc'])
    commit(types.TENANT_PROJECT_INSEPCTIONS, inspectionData)
    return inspectionData
  },
}

const mutations = {
  [types.TENANT_PROJECT_CURRENT] (state, { project }) {
    state.currentTenantProject = project || {}
  },
  [types.TENANT_PROJECT_UPDATE_FIELD_PATH] (state, { path, newValue }) {
    forceUpdateVueObject(state.currentTenantProject, path, newValue)
  },
  [types.SET_TENANT_PROJECT_SUBMISSION_BY_ID] (state, { sid, newValue }) {
    const submissions = lodash.get(state, 'currentTenantProject.submissions', [])
    const index = lodash.findIndex(submissions, {id: sid})
    if (index !== -1) {
      forceUpdateVueObject(state.currentTenantProject, ['submissions', index], newValue)
    }
  },
  [types.TP_UPDATE_TECH_COMMENTS_BY_ID] (state, { submissionId, sectionId, lineId, attributeName, newValue }) {
    const submissions = lodash.get(state, 'currentTenantProject.submissions', [])
    const sIndex = lodash.findIndex(submissions, {id: submissionId})
    if (sIndex > -1) {
      const path = ['submissions', sIndex]
      const comments = submissions[sIndex].techComment.comments
      const sectionIndex = lodash.findIndex(comments, {id: sectionId})
      if (sectionIndex > -1) {
        path.push(...['techComment', 'comments', sectionIndex])
        const lineIndex = lodash.findIndex(comments[sectionIndex].sectionContent, {id: lineId})
        if (lineIndex > -1) path.push(...['sectionContent', lineIndex])
      }
      path.push(attributeName)
      forceUpdateVueObject(state.currentTenantProject, path, newValue)
    }
  },
  [types.TENANT_PROJECT_RESET] (state) {
    state.currentTenantProject = {}
  },
  [types.TENANT_PROJECT_UPDATE_FIELD_NAME] (state, { name, value }) {
    updateVueObject(state.currentTenantProject, [name], value)
  },
  [types.TENANT_PROJECT_ACCOUNTS] (state, { value = [] }) {
    updateVueObject(state.currentTenantProject, ['accounts'], value)
  },
  [types.TENANT_PROJECT_LOAD_ALL] (state, { allTenantProjects }) {
    state.allTenantProjects = allTenantProjects
  },
  [types.TENANT_PROJECT_LOAD_VIEW_ALL] (state, { allTenantProjects }) {
    state.allTenantProjectsAll = allTenantProjects
  },
  [types.SET_LAST_SEARCH_ATTRIBUTES] (state, {lastSearchedAttributes}) {
    state.lastSearchedAttributes = lastSearchedAttributes
  },
  [types.SET_LAST_SEARCH_ATTRIBUTES_ALL] (state, {lastSearchedAttributesAll}) {
    state.lastSearchedAttributesAll = lastSearchedAttributesAll
  },
  [types.RESET_STATE] (state) {
    state.lastSearchedAttributesAll = {}
    state.lastSearchedAttributes = {}
    state.allTenantProjects = []
    state.allTenantProjectsAll = []
    state.startReview = false
    state.currentTenantProject = {
      accounts: [],
    }
  },
  [types.TENANT_PROJECT_SET_START_REVIEW] (state, {value}) {
    state.startReview = value
  },
  [types.TENANT_PROJECT_RESET_PROJECT_SUBMISSION] (state) {
    state.currentTenantProject = {
      accounts: [],
    }
    state.startReview = false
  },
  [types.TENANT_PROJECT_DEPOSIT] (state, deposit) {
    state.deposit = deposit
  },
  [types.TENANT_PROJECT_REFUND] (state, refund) {
    state.refund = refund
  },
  [types.TENANT_PROJECT_MANAGEMENT] (state, management) {
    state.management = management
  },
  [types.TENANT_PROJECT_INSEPCTIONS] (state, inspectionData) {
    state.inspections = inspectionData
  },
  [types.SET_CURR_TENANT_PROJECT_FINALDOCUMENT] (state, { finalDocument }) {
    forceUpdateVueObject(state.currentTenantProject, ['finalDocument'], finalDocument)
  },
  [types.TENANT_PROJECT_CLEAN_EMPTY_ACCOUNTS] (state) {
    state.currentTenantProject.accounts =
      state.currentTenantProject.accounts.filter(account => !checkEmptyAccount(account))
  },
  [types.SET_LAST_SEARCHED_KEYS] (state, { lastSearchKeys }) {
    state.lastSearchKeys = lodash.cloneDeep(lastSearchKeys)
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
